import { CreditorType } from "@gocardless/api/dashboard/types";
import {
  PlainButton,
  FontWeight,
  TypePreset,
  Glyph,
  Icon,
  Space,
  Box,
  Dialog,
  H2,
  P,
} from "@gocardless/flux-react";
import { Trans } from "@lingui/macro";
import { useToggle } from "react-use";

import { Route, routerPush } from "src/common/routing";
import { TrackingEvent } from "src/common/trackingEvents";
import { useSegment } from "src/technical-integrations/segment/useSegment";

interface BankDetailsDialogProps {
  sourceComponent: string;
  creditorType: CreditorType | undefined;
  handleAmendDetails?: () => void;
}

export const BankDetailsDialog = ({
  sourceComponent,
  creditorType,
  handleAmendDetails,
}: BankDetailsDialogProps) => {
  const { sendEvent } = useSegment();
  const [isOpen, toggleDialog] = useToggle(false);
  const handleDialogOpen = () => {
    toggleDialog();
    sendEvent(
      TrackingEvent.VERIFICATION_BANK_ACCOUNT_IN_DIFFERENT_NAME_DIALOG_OPENED,
      {
        source: sourceComponent,
      }
    );
  };

  const handleAmendDetailsOnClick = () => {
    sendEvent(
      TrackingEvent.VERIFICATION_CLICK_AMEND_BUSINESS_DETAILS_FROM_BANK_ACCOUNT_FORM,
      {
        source: sourceComponent,
      }
    );
    handleAmendDetails
      ? handleAmendDetails()
      : routerPush({ route: Route.BusinessDetails });
  };

  const amendYourBusinessDetails = (
    <PlainButton
      weight={FontWeight.SemiBold}
      preset={TypePreset.Body_02}
      decoration="underline"
      onClick={handleAmendDetailsOnClick}
    >
      <Trans>amend your business details</Trans>
    </PlainButton>
  );

  return (
    <Box className="fs-unmask">
      <PlainButton
        weight={FontWeight.SemiBold}
        preset={TypePreset.Body_02}
        decoration="underline"
        spaceAbove={1}
        onClick={handleDialogOpen}
      >
        <Trans>The bank account is in a different name</Trans>
        <Space h={0.5} layout="inline" />
        <Icon name={Glyph.Tooltip} verticalAlign="middle" />
      </PlainButton>

      <Dialog
        open={isOpen}
        aria-labelledby="account-in-different-name-dialog"
        closeAction={{
          label: <Trans>Close</Trans>,
          onClose: toggleDialog,
        }}
        header={
          <H2 preset={TypePreset.Heading_03}>
            <Trans>
              The bank account holder is different to the details I&apos;ve used
              for GoCardless
            </Trans>
          </H2>
        }
      >
        <P preset={TypePreset.Body_02} spaceBelow={1}>
          {creditorType === CreditorType.Individual ? (
            <Trans>
              If your bank account holder name does not match either your full
              name or trading name, we will not be able to automatically verify
              it — we may require further proof (such as a bank statement) that
              the bank account is owned by you and in your name.
            </Trans>
          ) : (
            <Trans>
              If your bank account holder name does not match the name of the
              legal entity entered earlier in the setup, we will not be able to
              automatically verify it for use with this business — please still
              enter your bank account details, but we may require further proof
              (such as a bank statement) that the bank account is in the name of
              your company.
            </Trans>
          )}
        </P>
        <P preset={TypePreset.Body_02} spaceBelow={1}>
          <Trans>
            If your bank account is in a different name, you may wish to{" "}
            {amendYourBusinessDetails}.
          </Trans>
        </P>
      </Dialog>
    </Box>
  );
};
